import type {
  ControllerPlacementCandidateInfo,
  ControllerPlacementCandidateTable,
} from "building-types/electrical/candidate";
import { DefaultPassRankingTable, explicitCandidateNew } from "building-types/electrical/candidate";
import { CandidateTypeConstraints } from "building-types/electrical/CandidateTypeConstraints";
import { CandidateType, ControllerPlacementPass } from "building-types/electrical/candidateTypesEnum";
import type { DoorCandidateTypeConstraints } from "building-types/electrical/DoorCandidateTypeConstraints";
import type { Room } from "building-types/rooms/roomTypes";
import type { RoomType } from "building-types/rooms/roomTypesEnum";
import type { SpaceType } from "building-types/rooms/spaceTypesEnum";
import type { Table } from "common/data/table";
import type { Vec2 } from "geom/vec2";
import { vec2Dist } from "geom/vec2";
import { addCandidateToTable } from "../controllerCandidates/addCandidateToTable";
import type { CandidateFilteringCriteria } from "../electricalLayoutTypes";
import type { DoorScore } from "../electricalRoomSetInfo";
import type { RoomSetSpaceTypesTable } from "../roomAndSpaceTypes/electricalSpaceTypes";
import type { PlacementPointAndNormal, QueryPointAndNormal } from "./controllersSelectionTypes";

export function primaryEntrancePlacement(
  doorAccessScores: DoorScore[],
  candidateTable: ControllerPlacementCandidateTable,
  setIndex: number,
  candidateFilteringCriteria: CandidateFilteringCriteria,
  proximityQueryApproxPosition?: Vec2 | null,
): ControllerPlacementCandidateInfo | null {
  const candidates = (candidateTable[setIndex] || []).filter((x) => filterCandidates(candidateFilteringCriteria, x));
  const chosenCandidates: ControllerPlacementCandidateInfo[] = [];

  let lowestDoorScore: number | undefined;
  for (const doorAccessScore of doorAccessScores) {
    const candidatesForDoor = candidates.filter(
      (x) => x.candidate.type === CandidateType.OPENING && x.candidate.sourceId === doorAccessScore.doorId,
    );
    if (candidatesForDoor.length > 0) {
      if (lowestDoorScore === undefined || doorAccessScore.score === lowestDoorScore) {
        chosenCandidates.push(...candidatesForDoor);
        lowestDoorScore = doorAccessScore.score;
      } else {
        break;
      }
    }
  }

  if (chosenCandidates.length > 0) {
    sortCandidatesByRankingTableAndOptionalProximity(chosenCandidates, proximityQueryApproxPosition);
    return chosenCandidates[0];
  }

  return null;
}

export function primaryEntranceOutsideRoomPlacement(
  doorScores: DoorScore[],
  candidateTable: ControllerPlacementCandidateTable,
  candidateFilteringCriteria: CandidateFilteringCriteria,
  proximityQueryApproxPosition?: Vec2 | null,
): ControllerPlacementCandidateInfo | null {
  let chosenDoorScore: DoorScore | undefined;
  for (const doorScore of doorScores) {
    const candidates = (candidateTable[doorScore.setIndex] || []).filter((x) =>
      filterCandidates(candidateFilteringCriteria, x),
    );
    const doorCands = candidates.filter(
      (x) => x.candidate.type === CandidateType.OPENING && x.candidate.sourceId === doorScore.doorId,
    );
    if (doorCands.length > 0) {
      chosenDoorScore = doorScore;
      break;
    }
  }

  if (chosenDoorScore) {
    const candidates = candidateTable[chosenDoorScore.setIndex] || [];
    const doorCands = candidates.filter(
      (x) => x.candidate.type === CandidateType.OPENING && x.candidate.sourceId === chosenDoorScore.doorId,
    );

    if (doorCands.length === 0) {
      return null;
    }

    sortCandidatesByRankingTableAndOptionalProximity(doorCands, proximityQueryApproxPosition);
    return doorCands[0];
  }

  return null;
}

export function nearestCandidate(
  queryPoint: Vec2 | null | undefined,
  roomSetIndex: number,
  candidateTable: ControllerPlacementCandidateTable,
  candidateTypeConstraints: CandidateTypeConstraints | undefined,
  candidateFilteringCriteria: CandidateFilteringCriteria,
): ControllerPlacementCandidateInfo | null {
  const roomSetCandidateInfos = (candidateTable[roomSetIndex] || []).filter((x) =>
    filterCandidates(candidateFilteringCriteria, x),
  );
  if (!roomSetCandidateInfos || roomSetCandidateInfos.length === 0) {
    return null;
  }
  const candidates = candidateTypeConstraints
    ? filterCandidatesByConstraints(candidateTypeConstraints, roomSetCandidateInfos)
    : roomSetCandidateInfos;

  sortCandidatesByProximity(queryPoint, candidates);

  if (candidates.length === 0) {
    // console.warn("closest door cannot be found by nearest candidate");
    return null;
  }

  return candidates[0];
}

export function nearestCandidateOutsideRoomSet(
  queryPoint: Vec2 | null | undefined,
  roomSetIndex: number,
  candidateTable: ControllerPlacementCandidateTable,
  candidateTypeConstraints: CandidateTypeConstraints | undefined,
  levelId: string,
  candidateFilteringCriteria: CandidateFilteringCriteria,
) {
  const otherRoomSetCandidateInfos = getCandidatesOutsideOfRoomSet(candidateTable, roomSetIndex, levelId).filter((x) =>
    filterCandidates(candidateFilteringCriteria, x),
  );

  const candidates = candidateTypeConstraints
    ? filterCandidatesByConstraints(candidateTypeConstraints, otherRoomSetCandidateInfos)
    : otherRoomSetCandidateInfos;

  if (candidates.length === 0) {
    return null;
  }

  sortCandidatesByProximity(queryPoint, candidates);

  if (candidates.length === 0) {
    // console.warn("closest door cannot be found by nearest candidate");
    return null;
  }

  return candidates[0];
}

export function nearestDoorOutsideRoomPlacement(
  queryPoint: Vec2 | null | undefined,
  doorScores: DoorScore[],
  candidateTable: ControllerPlacementCandidateTable,
  candidateFilteringCriteria: CandidateFilteringCriteria,
  doorCandidateTypeConstraints?: DoorCandidateTypeConstraints,
): ControllerPlacementCandidateInfo | null {
  const otherRoomSetCandidateInfos: ControllerPlacementCandidateInfo[] = [];

  for (const doorScore of doorScores) {
    const candidates = (candidateTable[doorScore.setIndex] || []).filter((x) =>
      filterCandidates(candidateFilteringCriteria, x),
    );
    const doorCands = candidates.filter(
      (x) => x.candidate.type === CandidateType.OPENING && x.candidate.sourceId === doorScore.doorId,
    );
    if (doorCands.length > 0) {
      otherRoomSetCandidateInfos.push(...doorCands);
      break;
    }
  }

  if (otherRoomSetCandidateInfos.length === 0) {
    return null;
  }

  const doorCands = doorCandidateTypeConstraints
    ? filterCandidatesByDoorConstraints(doorCandidateTypeConstraints, otherRoomSetCandidateInfos)
    : otherRoomSetCandidateInfos;

  if (doorCands.length === 0) {
    return null;
  }

  sortCandidatesByRankingTableAndOptionalProximity(doorCands, queryPoint);
  return doorCands[0];
}

export function byControllerTag(
  queryPoint: Vec2 | null | undefined,
  candidateTagLookup: { [tag: string]: ControllerPlacementCandidateInfo[] },
  tag: string | undefined,
  candidateTypeConstraints: CandidateTypeConstraints | undefined,
  candidateFilteringCriteria: CandidateFilteringCriteria,
) {
  const matchingCandidates = (
    tag && candidateTagLookup[tag] ? candidateTagLookup[tag].filter((x) => x.candidate) : []
  ).filter((x) => filterCandidates(candidateFilteringCriteria, x));

  if (!matchingCandidates || matchingCandidates.length === 0) {
    return null;
  }
  const candidates = candidateTypeConstraints
    ? filterCandidatesByConstraints(candidateTypeConstraints, matchingCandidates)
    : [...matchingCandidates];

  if (!candidates || candidates.length === 0) {
    return null;
  }

  sortCandidatesByProximity(queryPoint, candidates);

  return candidates[0];
}

export function doorByRoomType(
  queryPoint: Vec2 | null | undefined,
  doorScores: DoorScore[],
  candidateTable: ControllerPlacementCandidateTable,
  doorCandidateTypeConstraints: DoorCandidateTypeConstraints | undefined,
  roomTable: Table<Room>,
  spaceType: SpaceType | undefined,
  roomType: RoomType | undefined,
  spaceTypesTable: RoomSetSpaceTypesTable,
  roomSetIndex: number,
  candidateFilteringCriteria: CandidateFilteringCriteria,
): ControllerPlacementCandidateInfo | null {
  const doorIds: string[] = [];

  for (const doorScore of doorScores) {
    const candidates = (candidateTable[doorScore.oppSetIndex] || []).filter((x) =>
      filterCandidates(candidateFilteringCriteria, x),
    );
    const doorCands = candidates.filter((x) => {
      if (x.candidate.type !== CandidateType.OPENING) {
        return false;
      }
      if (x.candidate.sourceId !== doorScore.doorId) {
        return false;
      }
      if (!x.candidate.roomId || (spaceType && spaceTypesTable[x.candidate.roomId] !== spaceType)) {
        return false;
      }
      if (
        roomType &&
        x.candidate.toRoomId &&
        roomTable[x.candidate.toRoomId] &&
        roomTable[x.candidate.toRoomId].type !== roomType
      ) {
        return false;
      }
      return true;
    });

    if (doorCands.length > 0) {
      for (const c of doorCands) {
        if (c.candidate.type === CandidateType.OPENING) {
          doorIds.push(c.candidate.sourceId);
        }
      }
      break;
    }
  }

  const inRoomSetCandidates = candidateTable[roomSetIndex];
  if (!inRoomSetCandidates || inRoomSetCandidates.length === 0) {
    return null;
  }

  const matchingCands = inRoomSetCandidates.filter(
    (x) => x.candidate.type === CandidateType.OPENING && doorIds.includes(x.candidate.sourceId),
  );

  if (matchingCands.length === 0) {
    return null;
  }

  const doorCands = doorCandidateTypeConstraints
    ? filterCandidatesByDoorConstraints(doorCandidateTypeConstraints, matchingCands)
    : matchingCands;

  if (doorCands.length === 0) {
    return null;
  }

  sortCandidatesByRankingTableAndOptionalProximity(doorCands, queryPoint);
  return doorCands[0];
}

export function doorByRoomTypeOutside(
  queryPoint: Vec2 | null | undefined,
  doorScores: DoorScore[],
  candidateTable: ControllerPlacementCandidateTable,
  doorCandidateTypeConstraints: DoorCandidateTypeConstraints | undefined,
  roomTable: Table<Room>,
  spaceType: SpaceType | undefined,
  roomType: RoomType | undefined,
  spaceTypesTable: RoomSetSpaceTypesTable,
  candidateFilteringCriteria: CandidateFilteringCriteria,
): ControllerPlacementCandidateInfo | null {
  const otherRoomSetCandidateInfos: ControllerPlacementCandidateInfo[] = [];

  for (const doorScore of doorScores) {
    const candidates = (candidateTable[doorScore.setIndex] || []).filter((x) =>
      filterCandidates(candidateFilteringCriteria, x),
    );
    const doorCands = candidates.filter((x) => {
      if (x.candidate.type !== CandidateType.OPENING) {
        return false;
      }
      if (x.candidate.sourceId !== doorScore.doorId) {
        return false;
      }
      if (!x.candidate.roomId || (spaceType && spaceTypesTable[x.candidate.roomId] !== spaceType)) {
        return false;
      }
      if (roomType && roomTable[x.candidate.roomId] && roomTable[x.candidate.roomId].type !== roomType) {
        return false;
      }
      return true;
    });

    if (doorCands.length > 0) {
      otherRoomSetCandidateInfos.push(...doorCands);
      break;
    }
  }

  if (otherRoomSetCandidateInfos.length === 0) {
    return null;
  }

  const doorCands = doorCandidateTypeConstraints
    ? filterCandidatesByDoorConstraints(doorCandidateTypeConstraints, otherRoomSetCandidateInfos)
    : otherRoomSetCandidateInfos;

  if (doorCands.length === 0) {
    return null;
  }

  sortCandidatesByRankingTableAndOptionalProximity(doorCands, queryPoint);
  return doorCands[0];
}

export function doorByRoomIdPlacement(
  queryPoint: Vec2 | null | undefined,
  roomSetIndex: number,
  candidateTable: ControllerPlacementCandidateTable,
  oppositeRoomId: string | undefined,
  doorCandidateTypeConstraints: DoorCandidateTypeConstraints | undefined,
  candidateFilteringCriteria: CandidateFilteringCriteria,
) {
  const roomSetCandidateInfos = (candidateTable[roomSetIndex] || []).filter((x) =>
    filterCandidates(candidateFilteringCriteria, x),
  );
  if (!roomSetCandidateInfos || roomSetCandidateInfos.length === 0) {
    return null;
  }

  const candidates = roomSetCandidateInfos.filter(
    (x) => x.candidate.type === CandidateType.OPENING && x.candidate.toRoomId === oppositeRoomId,
  );

  const doorCands = doorCandidateTypeConstraints
    ? filterCandidatesByDoorConstraints(doorCandidateTypeConstraints, candidates)
    : candidates;

  if (doorCands.length === 0) {
    return null;
  }

  sortCandidatesByRankingTableAndOptionalProximity(doorCands, queryPoint);
  return candidates[0];
}

function filterCandidates(
  candidateFilteringCriteria: CandidateFilteringCriteria,
  x: ControllerPlacementCandidateInfo,
): boolean {
  if (x.candidate.type === CandidateType.OPENING) {
    return (
      !candidateFilteringCriteria.doorIdsToIgnore.includes(x.candidate.sourceId) &&
      !candidateFilteringCriteria.candidateIdsToIgnore.includes(x.candidate.id)
    );
  }

  return !candidateFilteringCriteria.candidateIdsToIgnore.includes(x.candidate.id);
}

export function addExplicitCandidate(
  queryPointAndNormal: QueryPointAndNormal,
  controllerPlacementCandidates: ControllerPlacementCandidateTable,
  snapCandidateTypeConstraints: CandidateTypeConstraints | undefined,
  snapToCandidateMaxSearchDistance: number,
  roomSetIndex: number,
  roomId: string,
  tag: string | undefined,
): ControllerPlacementCandidateInfo {
  const {
    levelId,
    point: [x, y],
    normal: [nX, nY],
  } = queryPointAndNormal;

  const roomSetCandidateInfos = controllerPlacementCandidates[roomSetIndex];

  if (!roomSetCandidateInfos || roomSetCandidateInfos.length === 0) {
    // no candidates, add one
    const candidate = explicitCandidateNew({
      tag,
      levelId: levelId,
      roomId,
      x,
      y,
      nX,
      nY,
    });

    const candidateInfo = addCandidateToTable(candidate, controllerPlacementCandidates, roomSetIndex);
    return candidateInfo;
  }

  const candidates = snapCandidateTypeConstraints
    ? filterCandidatesByConstraints(snapCandidateTypeConstraints, roomSetCandidateInfos)
    : roomSetCandidateInfos;

  if (candidates.length === 0) {
    // no candidates, add one
    const candidate = explicitCandidateNew({
      tag,
      levelId: levelId,
      roomId,
      x,
      y,
      nX,
      nY,
    });

    const candidateInfo = addCandidateToTable(candidate, controllerPlacementCandidates, roomSetIndex);
    return candidateInfo;
  }

  candidates.sort((a, b) => {
    const [aX, aY] = a.candidate.position;
    const [bX, bY] = b.candidate.position;
    return vec2Dist(aX, aY, x, y) - vec2Dist(bX, bY, x, y);
  });

  const [pX, pY] = candidates[0].candidate.position;
  if (vec2Dist(pX, pY, x, y) > snapToCandidateMaxSearchDistance) {
    const candidate = explicitCandidateNew({
      tag,
      levelId: levelId,
      roomId,
      x,
      y,
      nX,
      nY,
    });

    const candidateInfo = addCandidateToTable(candidate, controllerPlacementCandidates, roomSetIndex);
    return candidateInfo;
  }

  return candidates[0];
}

export function addExplicitPointCandidate(
  placementPointAndNormal: PlacementPointAndNormal,
  controllerPlacementCandidates: ControllerPlacementCandidateTable,
  roomSetIndex: number,
  roomId: string,
  tag: string | undefined,
): ControllerPlacementCandidateInfo {
  const {
    levelId,
    point: [x, y],
    normal: [nX, nY],
  } = placementPointAndNormal;

  const candidate = explicitCandidateNew({
    tag,
    levelId: levelId,
    roomId,
    x,
    y,
    nX,
    nY,
  });

  const candidateInfo = addCandidateToTable(candidate, controllerPlacementCandidates, roomSetIndex);
  return candidateInfo;
}

function getCandidatesOutsideOfRoomSet(
  candidateTable: ControllerPlacementCandidateTable,
  roomSetIndex: number,
  levelId: string,
) {
  const otherRoomSetCandidateInfos: ControllerPlacementCandidateInfo[] = [];
  for (const otherRoomSetIndexString in candidateTable) {
    const otherRoomSetIndex = parseInt(otherRoomSetIndexString);
    const cands = candidateTable[otherRoomSetIndex];
    if (otherRoomSetIndex !== roomSetIndex && cands) {
      for (const cand of cands) {
        if (cand.candidate.levelId !== levelId) {
          break;
        }
        otherRoomSetCandidateInfos.push(cand);
      }
    }
  }
  return otherRoomSetCandidateInfos;
}

function sortCandidatesByProximity(
  queryPoint: Vec2 | null | undefined,
  candidates: ControllerPlacementCandidateInfo[],
) {
  if (!queryPoint) {
    return;
  }
  const [x, y] = queryPoint;
  candidates.sort((a, b) => {
    const [aX, aY] = a.candidate.position;
    const [bX, bY] = b.candidate.position;
    return vec2Dist(aX, aY, x, y) - vec2Dist(bX, bY, x, y);
  });
}

function sortCandidatesByRankingTableAndOptionalProximity(
  doorCands: ControllerPlacementCandidateInfo[],
  proximityQueryApproxPosition: Vec2 | null | undefined,
) {
  if (proximityQueryApproxPosition) {
    doorCands.sort((a, b) => {
      const [aX, aY] = a.candidate.position;
      const [bX, bY] = b.candidate.position;
      const [pX, pY] = proximityQueryApproxPosition;
      return (
        DefaultPassRankingTable[a.candidate.insertionPass] - DefaultPassRankingTable[b.candidate.insertionPass] ||
        vec2Dist(aX, aY, pX, pY) - vec2Dist(bX, bY, pX, pY)
      );
    });
    return;
  }

  doorCands.sort((a, b) => {
    return DefaultPassRankingTable[a.candidate.insertionPass] - DefaultPassRankingTable[b.candidate.insertionPass];
  });
}

function filterCandidatesByConstraints(
  candidateTypeConstraints: CandidateTypeConstraints | undefined,
  roomSetCandidateInfos: ControllerPlacementCandidateInfo[],
) {
  let pass: ControllerPlacementPass;
  let secondPass: ControllerPlacementPass;
  let thirdPass: ControllerPlacementPass;

  switch (candidateTypeConstraints) {
    case CandidateTypeConstraints.BY_CONVEX_CORNER: {
      pass = ControllerPlacementPass.BY_CORNER;
      break;
    }
    case CandidateTypeConstraints.BY_DOOR: {
      pass = ControllerPlacementPass.BY_DOOR_LATCH_SIDE;
      secondPass = ControllerPlacementPass.BY_DOOR;
      break;
    }
    case CandidateTypeConstraints.BY_DOOR_BEHIND_SWING: {
      pass = ControllerPlacementPass.BY_DOOR_BEHIND_SWING;
      break;
    }
    case CandidateTypeConstraints.BY_DOOR_OR_BEHIND_SWING: {
      pass = ControllerPlacementPass.BY_DOOR_LATCH_SIDE;
      secondPass = ControllerPlacementPass.BY_DOOR;
      thirdPass = ControllerPlacementPass.BY_DOOR_BEHIND_SWING;
      break;
    }
    default:
  }

  const t = roomSetCandidateInfos.filter((x) => {
    if (secondPass !== undefined && thirdPass !== undefined) {
      return (
        x.candidate.insertionPass === pass ||
        x.candidate.insertionPass === secondPass ||
        x.candidate.insertionPass === thirdPass
      );
    }

    if (secondPass !== undefined) {
      return x.candidate.insertionPass === pass || x.candidate.insertionPass === secondPass;
    }
    return x.candidate.insertionPass === pass;
  });

  return t;
}

function filterCandidatesByDoorConstraints(
  candidateTypeConstraints: DoorCandidateTypeConstraints,
  roomSetCandidateInfos: ControllerPlacementCandidateInfo[],
) {
  let pass: ControllerPlacementPass;
  let secondPass: ControllerPlacementPass;
  switch (candidateTypeConstraints) {
    case CandidateTypeConstraints.BY_DOOR: {
      pass = ControllerPlacementPass.BY_DOOR_LATCH_SIDE;
      secondPass = ControllerPlacementPass.BY_DOOR;
      break;
    }
    case CandidateTypeConstraints.BY_DOOR_BEHIND_SWING: {
      pass = ControllerPlacementPass.BY_DOOR_BEHIND_SWING;
      break;
    }
  }
  return roomSetCandidateInfos.filter((x) => {
    if (secondPass !== undefined) {
      return x.candidate.insertionPass === pass || x.candidate.insertionPass === secondPass;
    }
    return x.candidate.insertionPass === pass;
  });
}
